import { useCallback, useMemo, useState } from "react";
import { Button } from "@legal-500/legal500-design-system";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  APPLY_CHUNK_SIZE,
  roleProvisioningService,
  type ApplyRowResult,
  type ScanResponse,
} from "../../../api/roleProvisioning.service.js";
import { ToolNotice } from "./roleProvisioning/ToolNotice.js";
import { OwnerWarning } from "./roleProvisioning/OwnerWarning.js";
import { UploadStep } from "./roleProvisioning/UploadStep.js";
import { PreviewTable } from "./roleProvisioning/PreviewTable.js";
import { ResultsStep } from "./roleProvisioning/ResultsStep.js";
import { buildPreviewRows, isSelectable } from "./roleProvisioning/previewRows.js";
import { buildApplyRows, runChunkedApply } from "./roleProvisioning/runApply.js";
import { useCsvUpload } from "./roleProvisioning/useCsvUpload.js";
import styles from "./roleProvisioning/RoleProvisioning.module.css";

/**
 * Role Provisioning (migration-plan.md): grant account-services roles to a CS
 * list of emails. Upload → scan (read-only dry-run on users-api) → tick the
 * rows → confirmed, chunked apply → results with skipped.csv and applied.csv.
 *
 * Phases: idle → parsed → scanning → scanned → applying → done. "parsed" is
 * derived — the upload hook owns the parse — so there is one source of truth.
 */

type Phase = "idle" | "parsed" | "scanning" | "scanned" | "applying" | "done";

type ApplyProgress = { done: number; total: number };

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export default function RoleProvisioningTab() {
  const { hasPermission } = usePermissions();
  const upload = useCsvUpload();

  const [phase, setPhase] = useState<Exclude<Phase, "parsed">>("idle");
  const [scan, setScan] = useState<ScanResponse | null>(null);
  /** Line numbers of the ticked rows. */
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [includeOwners, setIncludeOwners] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [applyProgress, setApplyProgress] = useState<ApplyProgress>({ done: 0, total: 0 });
  const [applied, setApplied] = useState<ApplyRowResult[] | null>(null);

  const effectivePhase: Phase = phase === "idle" && upload.parsed ? "parsed" : phase;
  const isBusy = phase === "scanning" || phase === "applying";

  const runScan = useCallback(async () => {
    setError(null);
    setApplied(null);
    setScan(null);
    setSelected(new Set());
    setIncludeOwners(false);
    setPhase("scanning");
    try {
      const response = await roleProvisioningService.scan(upload.roles, upload.built.rows);
      setScan(response);
      // Insert rows start selected; create and account-owner rows are opt-in.
      setSelected(
        new Set(response.rows.filter(row => row.outcome === "insert").map(row => row.lineNumber)),
      );
      setPhase("scanned");
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : String(scanError));
      setPhase("idle");
    }
  }, [upload.roles, upload.built.rows]);

  const toggleRow = useCallback((lineNumber: number) => {
    setSelected(current => {
      const next = new Set(current);
      if (!next.delete(lineNumber)) {
        next.add(lineNumber);
      }
      return next;
    });
  }, []);

  const setRows = useCallback((lineNumbers: number[], isSelected: boolean) => {
    setSelected(current => {
      const next = new Set(current);
      for (const lineNumber of lineNumbers) {
        if (isSelected) {
          next.add(lineNumber);
        } else {
          next.delete(lineNumber);
        }
      }
      return next;
    });
  }, []);

  const scanRows = scan?.rows ?? [];

  /** The toggle also (de)selects every owner row, so it reads as one decision. */
  const changeIncludeOwners = useCallback(
    (include: boolean) => {
      setIncludeOwners(include);
      setRows(
        scanRows
          .filter(row => row.outcome === "covered_by_account_owner")
          .map(row => row.lineNumber),
        include,
      );
    },
    [scanRows, setRows],
  );

  const previewRows = useMemo(
    () => buildPreviewRows(scanRows, upload.built.problems),
    [scanRows, upload.built.problems],
  );

  /** What this run will send: ticked AND still selectable under the current toggle. */
  const selectedScanRows = useMemo(
    () =>
      scanRows.filter(
        row => selected.has(row.lineNumber) && isSelectable(row.outcome, includeOwners),
      ),
    [scanRows, selected, includeOwners],
  );
  const selectedCount = selectedScanRows.length;
  const createCount = selectedScanRows.filter(row => row.outcome === "create_and_insert").length;
  const organisationCount = new Set(selectedScanRows.map(row => row.organisationId)).size;

  const applyRoles = useCallback(async () => {
    setIsConfirming(false);
    setError(null);
    setPhase("applying");

    const rows = buildApplyRows(
      selectedScanRows,
      new Set(selectedScanRows.map(row => row.lineNumber)),
    );
    const { results, failure } = await runChunkedApply(upload.roles, rows, (done, total) =>
      setApplyProgress({ done, total }),
    );

    if (results.length === 0 && failure) {
      // Nothing came back at all, so there is no undo list to protect and no
      // result worth showing: back to the preview with the selection intact.
      setError(failure);
      setPhase("scanned");
      return;
    }
    setApplied(results);
    setError(failure);
    setPhase("done");
  }, [selectedScanRows, upload.roles]);

  if (!hasPermission("developer.write")) {
    return <UnauthorizedView message="You do not have permission to use Role Provisioning." />;
  }

  const rolesLabel = upload.roles.join(", ");
  // Owner mode never creates accounts (§4.5), so the confirmation drops the create count.
  const confirmMessage = upload.ownerMode
    ? `Grant account_owner (full access) to ${plural(selectedCount, "user")} in ` +
      `${plural(organisationCount, "organisation")}?`
    : `Grant ${rolesLabel} to ${plural(selectedCount, "user")} in ` +
      `${plural(organisationCount, "organisation")} and create ` +
      `${plural(createCount, "account")}?`;
  const confirmWarning = upload.ownerMode
    ? "Account Owner is a wildcard: full access to every current and future feature of the " +
      "organisation, including user management. Download applied.csv after the run as the undo list."
    : "Created users get no email — they log in with an email OTP, so CS must tell them. " +
      "Roles are added with source 'manual'; download applied.csv after the run as the undo list.";
  const showUpload =
    effectivePhase === "idle" || effectivePhase === "parsed" || phase === "scanning";

  return (
    <div className={styles.stack}>
      <ToolNotice />

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      {showUpload ? (
        <UploadStep upload={upload} disabled={isBusy} onScan={() => void runScan()} />
      ) : null}

      {phase === "scanning" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Scanning…</div>
        </div>
      ) : null}

      {phase === "applying" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Applying…</div>
          <div className={styles.progress} role="status" aria-live="polite">
            {`${applyProgress.done.toLocaleString()} / ${applyProgress.total.toLocaleString()} rows`}
          </div>
          <div className={styles.rowNote}>
            {applyProgress.total > APPLY_CHUNK_SIZE
              ? `Sent in batches of ${APPLY_CHUNK_SIZE.toLocaleString()}. Keep this tab open — applied.csv is built from the responses.`
              : "Keep this tab open — applied.csv is built from the responses."}
          </div>
        </div>
      ) : null}

      {phase === "scanned" && scan ? (
        <>
          {upload.ownerMode ? <OwnerWarning /> : null}
          <div className={styles.card}>
            <div className={styles.cardTitle}>3 · Review the dry-run</div>
            <PreviewTable
              rows={previewRows}
              selected={selected}
              includeOwners={includeOwners}
              disabled={isBusy}
              onToggleRow={toggleRow}
              onSetRows={setRows}
              onIncludeOwnersChange={changeIncludeOwners}
            />
          </div>
          <div className={styles.toolbar}>
            <Button variant="outlined" disabled={isBusy} onClick={() => setPhase("idle")}>
              Back to upload
            </Button>
            <span className={styles.resultCount}>
              {plural(selectedCount, "row")} selected · roles: {rolesLabel}
            </span>
            <span className={styles.spacer} />
            <Button
              variant="contained"
              disabled={selectedCount === 0 || isBusy}
              onClick={() => setIsConfirming(true)}
            >
              Apply {selectedCount} selected
            </Button>
          </div>
        </>
      ) : null}

      {phase === "done" && applied ? (
        <ResultsStep
          applied={applied}
          scanRows={scanRows}
          previewRows={previewRows}
          parsed={upload.parsed}
          onScanAgain={() => void runScan()}
        />
      ) : null}

      {isConfirming ? (
        <ConfirmationModal
          title="Grant roles"
          message={confirmMessage}
          warningMessage={confirmWarning}
          confirmButtonText={`Apply ${plural(selectedCount, "row")}`}
          // A stray click beside the dialog must not stand in for a decision
          // about hundreds of user rows — Cancel and Escape still do.
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={applyRoles}
        />
      ) : null}
    </div>
  );
}
